import React, {useEffect, useMemo, useState} from "react";
import {
    createCategoryRequest,
    type CategoryRequestCreateDTO,
    type CategoryRequestDTO,
} from "../../api/CategoryRequestAPI";
import { getUserMessage, logApiError } from "../../api/apiError";

export type { CategoryRequestDTO };

type Props = {
    token?: string;
    defaultName?: string;   // ✅ ADD THIS
    onCreated?: (created: CategoryRequestDTO) => void;
    onCancel?: () => void;
};

const styles: Record<string, React.CSSProperties> = {
    panel: {
        border: "1px solid #ddd",
        borderRadius: 10,
        padding: 16,
        maxWidth: 720,
        background: "#fff",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
    },
    title: { margin: 0, fontSize: 18 },
    hint: { margin: 0, fontSize: 12, color: "#666" },
    form: { display: "flex", flexDirection: "column", gap: 12 },
    row: { display: "flex", flexDirection: "column", gap: 6 },
    label: { fontWeight: 700, fontSize: 13 },
    input: {
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid #bbb",
        fontSize: 14,
    },
    textarea: {
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid #bbb",
        fontSize: 14,
        minHeight: 140,
        resize: "vertical",
    },
    actions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" as const },
    error: { color: "#b00020", fontSize: 13, margin: 0 },
    ok: { color: "#0a7a2f", fontSize: 13, margin: 0 },
    counter: { fontSize: 12, color: "#666", textAlign: "right" },
};

export default function CategoryRequestPanel({
                                                 token,
                                                 defaultName,
                                                 onCreated,
                                                 onCancel,
                                             }: Props) {
    const [name, setName] = useState(defaultName ?? "");
    const [text, setText] = useState("");
    //const [requestersId, setRequestersId] = useState<string>(""); // optional numeric
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string>("");
    const [okMsg, setOkMsg] = useState<string>("");

    useEffect(() => {
        if (defaultName) {
            setName(defaultName);
        }
    }, [defaultName]);



    /** OpenAPI `CategoryRequestCreateDTO` requires only `name`; `text` is optional. */
    const canSubmit = useMemo(() => {
        return name.trim().length >= 2 && !loading;
    }, [name, loading]);

    const reset = () => {
        setName("");
        setText("");
        //setRequestersId("");
    };

    const submit = async () => {
        setErr("");
        setOkMsg("");

        const payload: CategoryRequestCreateDTO = {
            name: name.trim(),
            ...(text.trim() ? { text: text.trim() } : {}),
        };

        try {
            setLoading(true);

            // ✅ Change this path to match your backend:
            // e.g. "/api/category-requests" or "/categoryRequest"
            const created = await createCategoryRequest(payload, token);
            setOkMsg("Request submitted.");
            reset();
            onCreated?.(created);
        } catch (e: any) {
            logApiError(e);
            setErr(getUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.panel}>
            <div style={styles.headerRow}>
                <div>
                    <h3 style={styles.title}>Request a new category</h3>
                    <p style={styles.hint}>
                        Category name is required. Add details below if you want (recommended).
                    </p>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, color: "#666" }}>
                        {token ? "Signed in" : "Anonymous"}
                    </div>
                </div>
            </div>

            <div style={styles.form}>

                <div style={styles.row}>
                    <label style={styles.label}>Category name</label>
                    <input
                        style={styles.input}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Aircraft, Heavy Equipment, Medical Device..."
                    />
                    <div style={styles.counter}>{name.trim().length} chars</div>
                </div>

                <div style={styles.row}>
                    <label style={styles.label}>Description / details</label>
                    <textarea
                        style={styles.textarea}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={
                            "Explain what this category is.\n" +
                            "Include example parameters (Make, Model, Year, etc.), and any validation rules."
                        }
                    />
                    <div style={styles.counter}>{text.trim().length} chars</div>
                </div>

                <div style={styles.actions}>
                    <button
                        type="button"
                        disabled={!canSubmit}
                        onClick={submit}
                        title={!canSubmit ? "Enter a category name (at least 2 characters)" : "Submit"}
                    >
                        {loading ? "Submitting..." : "Submit request"}
                    </button>

                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => {
                            setErr("");
                            setOkMsg("");
                            reset();
                        }}
                        disabled={loading}
                    >
                        Clear
                    </button>
                </div>

                {err ? <p style={styles.error}>{err}</p> : null}
                {!err && okMsg ? <p style={styles.ok}>{okMsg}</p> : null}
            </div>
        </div>
    );
}